import { downloadFileDirectly } from "@/lib/api/utils";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { formatFileSize } from "@/utils/number/si-prefix";

import { Card, CardContent } from "../Card/card";
import Icon from "../Icon/Icon";
import { HStack, VStack } from "../Layout";
import { Text } from "../Typography/Text";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";

import type { BaseProps, FileItem } from "./types";

type DropZoneFileProps = BaseProps & {
  fileItems?: FileItem[];
  onRemoveFile?: (id: string) => void;
  disabled?: boolean;
  helpText?: string;
};

export const DropZoneFile = ({
  getRootProps,
  getInputProps,
  isDragActive,
  uploadError,
  fileItems = [],
  onRemoveFile,
  disabled,
  helpText,
}: DropZoneFileProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.DropZone",
  });

  const onDownloadFile = (fileItem: FileItem) => {
    downloadFileDirectly(
      fileItem.file instanceof File
        ? URL.createObjectURL(fileItem.file)
        : fileItem.file.url,
      fileItem.name,
    );
  };

  return (
    <VStack className="gap-3 overflow-x-hidden">
      {/* ドロップゾーン */}
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
          isDragActive
            ? "border-primary-main bg-primary-muted-fill"
            : "border-neutral-muted-on-fill",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <input {...getInputProps()} />
        <VStack className="gap-2 items-center">
          <Icon
            iconName="FileUpIcon"
            className={cn(
              "w-8 h-8",
              isDragActive ? "text-primary-main" : "text-neutral-muted-on-fill",
            )}
          />
          <Text variant="labelSmall">
            {isDragActive
              ? t("file.dropActiveMessage")
              : t("file.dropInactiveMessage")}
          </Text>
          {helpText && (
            <Text
              variant="labelSmallReg"
              className="text-neutral-muted-on-fill"
            >
              {helpText}
            </Text>
          )}
        </VStack>
      </div>

      {/* ファイル一覧 */}
      {fileItems.length > 0 && (
        <VStack className="gap-2 min-w-0 overflow-x-hidden">
          {fileItems.map((fileItem) => (
            <Card key={fileItem.id} className="w-full min-w-0 overflow-hidden">
              <CardContent className="p-3 min-w-0">
                <HStack className="gap-3 items-center min-w-0">
                  {fileItem.preview ? (
                    <img
                      src={fileItem.preview}
                      alt="preview"
                      className="w-10 h-10 object-cover rounded border"
                    />
                  ) : (
                    <Icon
                      iconName="FileXIcon"
                      className="w-10 h-10 text-neutral-muted-on-fill"
                    />
                  )}
                  <VStack className="flex-1 min-w-0 gap-1">
                    <Text variant="labelSmall" className="truncate">
                      {fileItem.name}
                    </Text>
                    <Text
                      variant="labelSmallReg"
                      className="text-neutral-muted-on-fill"
                    >
                      {formatFileSize(fileItem.size)}
                    </Text>
                  </VStack>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDownloadFile(fileItem)}
                  >
                    <Icon iconName="Download" />
                  </Button>
                  {onRemoveFile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveFile(fileItem.id)}
                      disabled={disabled}
                    >
                      <Icon iconName="XIcon" />
                    </Button>
                  )}
                </HStack>
              </CardContent>
            </Card>
          ))}
        </VStack>
      )}

      {/* エラー表示 */}
      {uploadError && (
        <Alert variant="destructive">
          <Icon iconName="AlertCircle" />
          <AlertTitle>{t("errorTitle")}</AlertTitle>
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}
    </VStack>
  );
};
